import { css } from '@emotion/react';
import { Link,withPrefix } from 'gatsby';
import { COMPANY_SITE_URL, PRIVACY_POLICY_URL } from '@/constants/url';
import { pagesPath } from '@/lib/pagesPath';
import { getScreen } from '@/styles/media-query';

export const Footer: React.FC = () => {
  return (
    <footer
      css={css`
        background: #24312f;
        color: #fff;

        @media ${getScreen('pc')} {
          display: grid;
          grid-template-areas: '. area1 area2 area3';
          grid-template-columns: 200fr 600fr 330fr 300fr;
          height: 425px;
        }
      `}
    >
      <nav
        css={css`
          grid-area: area2;
          display: grid;
          grid-template-rows: 160px 144px;

          @media ${getScreen('pc')} {
            grid-template-rows: 1fr 1fr;
          }
        `}
      >
        {[
          {
            title: 'Uniforceをご利用中のお客様',
            to: pagesPath.contact.$url(),
            bg: `url(${withPrefix('/images/footer-button-1.png')})`,
          },
          {
            title: 'お問い合わせ',
            to: pagesPath.contact.$url(),
            bg: `url(${withPrefix('/images/footer-button-2.png')})`,
          },
        ].map((item, i) => (
          <Link
            key={i}
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              background: ${item.bg} center center/cover;
              height: 100%;
            `}
            to={item.to}
          >
            <p
              css={css`
                text-align: center;
                font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
                letter-spacing: 0.56px;
                color: #ffffff;
                text-shadow: 0px 0px 10px #0000001a;
              `}
            >
              {item.title}
            </p>
          </Link>
        ))}
      </nav>
      <nav
        css={css`
          padding: 60px 0 0;

          @media ${getScreen('pc')} {
            padding: 60px 0 30px;
            display: grid;
            grid-template-rows: 1fr auto auto auto;
            grid-area: area1;
          }
        `}
      >
        <ul
          css={css`
            padding: 0 20px;
            list-style: none;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 45px 0;
            width: 100%;

            @media ${getScreen('pc')} {
              grid-template-columns: repeat(3, 1fr);
              padding: 0;
              height: max-content;
            }
          `}
        >
          {[
            { title: 'サービスの特徴', to: pagesPath.features.$url() },
            { title: '主な機能', to: pagesPath.tool.$url() },
            { title: '料金プラン', to: pagesPath.plan.$url() },
            { title: 'お知らせ', to: pagesPath.news.$url() },
            { title: 'よくあるご質問', to: pagesPath.faq.$url() },
            { title: 'セキュリティ', to: pagesPath.security.$url() },
          ].map((item, i) => (
            <li key={i}>
              <Link
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 6px;
                `}
                to={item.to}
              >
                <span
                  css={css`
                    display: flex;
                    align-items: center;
                    width: 8px;
                    height: 13px;
                  `}
                >
                  <img
                    src={withPrefix('/images/footer-arrow.svg')}
                    width="8"
                    height="13"
                    alt=""
                  />
                </span>
                <span>{item.title}</span>
              </Link>
            </li>
          ))}
        </ul>
        <div
          css={css`
            margin: 45px 0 24px;
            padding: 0 20px;

            @media ${getScreen('pc')} {
              margin: 0;
              padding: 0;
            }
          `}
        >
          <a
            css={css`
              display: block;
              width: max-content;
            `}
            href="https://www.cscloud.co.jp/dx-security"
            target="_blank"
            rel="noreferrer"
          >
            <img
              css={css`
                width: auto;
                height: 53px;
              `}
              src={withPrefix('/images/footer-banner.jpg')}
              alt="日本のDXをもっと安全に"
              width="304"
              height="107"
            />
          </a>
        </div>
        <ul
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            border-top: 1px solid #ffffff;
            padding: 30px;

            @media ${getScreen('pc')} {
              gap: 20px;
              flex-direction: row;
              margin-top: 30px;
              padding: 0;
              border: none;
            }
          `}
        >
          {[
            {
              title: '運営会社',
              to: COMPANY_SITE_URL,
              isExternal: true,
            },
            {
              title: 'プライバシーポリシー',
              to: PRIVACY_POLICY_URL,
              isExternal: true,
            },
            {
              title: '利用規約',
              to: pagesPath.rule.$url(),
              isExternal: false,
            },
          ].map((item, i) => (
            <li
              key={i}
              css={css`
                font: normal normal normal 13px/19px 'Noto Sans JP', sans-serif;
                letter-spacing: 0.52px;
                color: #ffffff;

                @media ${getScreen('pc')} {
                  border-right: 1px solid #fff;
                  padding-right: 20px;
                }

                &:last-of-type {
                  @media ${getScreen('pc')} {
                    border-right: none;
                    padding-right: 0;
                  }
                }
              `}
            >
              {item.isExternal ? (
                <a href={item.to} target="_blank" rel="noreferrer">
                  {item.title}
                </a>
              ) : (
                <Link to={item.to}>{item.title}</Link>
              )}
            </li>
          ))}
        </ul>
        <p
          css={css`
            font: normal normal normal 9px/12px 'din-2014', sans-serif;
            letter-spacing: 0.18px;
            color: #ffffff;
            margin-top: 8px;

            @media ${getScreen('pc', '未満')} {
              display: none;
            }
          `}
        >
          © Uniforce All Right Reserved.
        </p>
      </nav>
      <div
        css={css`
          background: #111917;
          padding: 40px 0 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 30px;
          grid-area: area3;
        `}
      >
        <Link
          to={pagesPath.$url()}
          css={css`
            display: block;
            width: max-content;
          `}
        >
          <img
            src={withPrefix('/images/logo-vertical.svg')}
            alt="Uniforce(ユニフォース) IPO準備クラウド"
            width="116"
            height="153"
          />
        </Link>
        <p
          css={css`
            font: normal normal normal 10px/13px 'din-2014', sans-serif;
            letter-spacing: 0.4px;
            color: #ffffff;

            @media ${getScreen('pc')} {
              display: none;
            }
          `}
        >
          © Uniforce All Right Reserved.
        </p>
      </div>
    </footer>
  );
};
